import { useCallback, useEffect, useRef, useState } from 'react';
import { copierTexte } from '../services/copie';

export type EtatCopie = 'repos' | 'copie' | 'echec';

/** Durée pendant laquelle « Copié » (ou l'échec) reste affiché avant de revenir à « Copier ». */
export const DELAI_ETAT_COPIE_MS = 2000;

/**
 * État d'un bouton « Copier ». « Copié » apparaît puis disparaît seul ; une nouvelle copie relance
 * le délai au lieu de laisser l'ancien minuteur effacer le nouvel état. Un partage (repli) ne passe
 * pas par « Copié » : voir `copierTexte`.
 */
export function useCopie(delaiMs: number = DELAI_ETAT_COPIE_MS) {
  const [etat, setEtat] = useState<EtatCopie>('repos');
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  const monte = useRef(true);
  const enCours = useRef(false);

  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
      if (minuteur.current) clearTimeout(minuteur.current);
    };
  }, []);

  const copier = useCallback(
    async (texte: string) => {
      // Deux appuis rapprochés ouvriraient deux feuilles de partage (module absent) : la seconde
      // serait refusée et afficherait « Échec » pendant que la première est ouverte.
      if (enCours.current) return 'partage' as const;
      enCours.current = true;
      let resultat: Awaited<ReturnType<typeof copierTexte>>;
      try {
        resultat = await copierTexte(texte);
      } finally {
        enCours.current = false;
      }
      if (!monte.current) return resultat;
      if (minuteur.current) clearTimeout(minuteur.current);
      if (resultat === 'partage') {
        setEtat('repos');
        return resultat;
      }
      setEtat(resultat === 'copie' ? 'copie' : 'echec');
      minuteur.current = setTimeout(() => setEtat('repos'), delaiMs);
      return resultat;
    },
    [delaiMs],
  );

  return { etat, copier };
}
